import { useState } from 'react';
import React from 'react';

import { type PaymentFormTotals } from 'src/app/components/checkout/types';
import { useCart } from 'src/queries/curbside/get-cart';
import { useAppSelector } from 'src/utils';
import { useHasPreauth } from 'src/utils/hooks/use-has-preauth';
import { useIsDutchiePay } from 'src/utils/hooks/use-is-dutchie-pay';

export enum FeePaymentMethod {
  DutchiePay = 1,
  PinDebit = 2,
  Credit = 3,
}

type UsePaymentReturn = {
  handlePaymentFailure: () => void;
  handlePaymentSuccess: () => void;
  showCheckoutSuccess: boolean;
  totals: PaymentFormTotals;
};

export const usePayment = (): UsePaymentReturn => {
  // Global state
  const changeAmountDue = useAppSelector((state) => state.mobileCheckout.changeAmountDue) ?? 0;
  const changeAmountRounded = useAppSelector((state) => state.mobileCheckout.changeAmountRounded) ?? 0;
  const totalAmountPaid = useAppSelector((state) => state.mobileCheckout.totalAmountPaid) ?? 0;
  const isCheckoutComplete = useAppSelector((state) => state.mobileCheckout.isCheckoutComplete);
  const activeCart = useAppSelector((state) => state.mobileCheckout.activeCart);

  // Local state
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(isCheckoutComplete);

  // Hooks
  const hasPreauth = useHasPreauth();
  const isDutchiePay = useIsDutchiePay();
  const { data: cart } = useCart();

  // Use cart data from Redux store if available, otherwise fall back to API data
  const cartData = activeCart || cart;

  // Computed
  const totals: PaymentFormTotals = React.useMemo(
    () => ({
      total: cartData?.GrandTotalRounded ?? 0,
      subtotal: cartData?.SubTotal ?? 0,
      tax: cartData?.Tax ?? 0,
      discount: cartData?.TotalDiscountAndLoyalty ?? 0,
      rounding: cartData?.RoundingAmount ?? 0,
      feesDonations: cartData?.FeesAndDonations ?? 0,
      changeDue: hasPreauth ? 0 : changeAmountDue,
      changeRounded: hasPreauth ? 0 : changeAmountRounded,
      totalPaid: hasPreauth ? (cartData?.GrandTotalRounded ?? 0) : totalAmountPaid,
      tip: cartData?.TipAmount ?? 0,
      dutchiePayFees: isDutchiePay
        ? ((cartData?.FeesDonations ?? [])
            .filter((fd) => fd.FeePaymentMethod === FeePaymentMethod.DutchiePay)
            .map((fd) => fd.CashValue)
            .reduce((sum, cv) => sum + cv, 0) ?? 0)
        : 0,
      nonCashAdjustmentFees:
        (cartData?.FeesByPaymentType ?? [])
          .filter((fd) => fd.FeePaymentMethod === FeePaymentMethod.PinDebit)
          .map((fd) => fd.CashValue)
          .reduce((sum, cv) => sum + cv, 0) ?? 0,
      creditNonCashAdjustmentFees:
        (cartData?.FeesByPaymentType ?? [])
          .filter((fd) => fd.FeePaymentMethod === FeePaymentMethod.Credit)
          .map((fd) => fd.CashValue)
          .reduce((sum, cv) => sum + cv, 0) ?? 0,
    }),
    [cartData, isDutchiePay, hasPreauth, changeAmountDue, totalAmountPaid, changeAmountRounded],
  );

  // Handlers
  const handlePaymentSuccess = () => {
    setShowCheckoutSuccess(true);
  };

  const handlePaymentFailure = () => {
    setShowCheckoutSuccess(false);
  };

  return {
    totals,
    showCheckoutSuccess,
    handlePaymentSuccess,
    handlePaymentFailure,
  };
};
